const CARD_REFERENCE_WIDTH_MM = 85.6;
const CARD_REFERENCE_HEIGHT_MM = 53.98;
// V3 intentionally starts a fresh calibration: the former full-card UI could
// leave a visually plausible but physically incorrect value on mobile.
const CALIBRATION_STORAGE_KEY = 'arara-size-fit-pixels-per-mm-v3';

// GIA international ring size chart: US/Canada, UK/Australia, selected Asian
// markets, inner diameter and circumference. The exact circumference also
// provides the ISO 8653 measurement reference shown in the interface.
// One centralized source is used by every ring measurement method.
const RING_SIZE_TABLE = Object.freeze([
  { size: 3, diameter: 14.1, circumference: 44.3, uk: 'F', asia: '4' },
  { size: 3.5, diameter: 14.5, circumference: 45.6, uk: 'G', asia: null },
  { size: 4, diameter: 14.9, circumference: 46.8, uk: 'H', asia: '7' },
  { size: 4.5, diameter: 15.3, circumference: 48.1, uk: 'I', asia: '8' },
  { size: 5, diameter: 15.7, circumference: 49.3, uk: 'J', asia: '9' },
  { size: 5.5, diameter: 16.1, circumference: 50.6, uk: 'K', asia: '10' },
  { size: 6, diameter: 16.5, circumference: 51.8, uk: 'L', asia: '11' },
  { size: 6.5, diameter: 16.9, circumference: 53.1, uk: 'M', asia: '13' },
  { size: 7, diameter: 17.3, circumference: 54.3, uk: 'N', asia: '14' },
  { size: 7.5, diameter: 17.7, circumference: 55.6, uk: 'O', asia: '15' },
  { size: 8, diameter: 18.1, circumference: 56.9, uk: 'P', asia: '16' },
  { size: 8.5, diameter: 18.5, circumference: 58.1, uk: 'Q', asia: '17' },
  { size: 9, diameter: 19, circumference: 59.7, uk: 'R', asia: '18' },
  { size: 9.5, diameter: 19.4, circumference: 60.9, uk: 'S', asia: '19' },
  { size: 10, diameter: 19.8, circumference: 62.2, uk: 'T', asia: '20' },
  { size: 10.5, diameter: 20.2, circumference: 63.5, uk: 'U', asia: '22' },
  { size: 11, diameter: 20.6, circumference: 64.7, uk: 'V', asia: '23' },
  { size: 11.5, diameter: 21, circumference: 66, uk: 'W', asia: '24' },
  { size: 12, diameter: 21.4, circumference: 67.2, uk: 'X', asia: '25' },
  { size: 12.5, diameter: 21.8, circumference: 68.5, uk: 'Y', asia: '26' },
  { size: 13, diameter: 22.2, circumference: 69.7, uk: 'Z½', asia: '27' },
]);

// Reserved architecture for a future product-level profile. V1 deliberately
// activates no numeric fit allowance for unknown bracelet structures.
const BRACELET_FIT_PROFILES = Object.freeze({
  unknown: Object.freeze({ recommendsLength: false, allowancesCm: null }),
  chain: Object.freeze({ recommendsLength: false, allowancesCm: null }),
  tennis: Object.freeze({ recommendsLength: false, allowancesCm: null }),
  bangle: Object.freeze({ recommendsLength: false, allowancesCm: null }),
  cuff: Object.freeze({ recommendsLength: false, allowancesCm: null }),
  adjustable: Object.freeze({ recommendsLength: false, allowancesCm: null }),
});

const FIT_COPY = Object.freeze({
  close: 'A closer fit generally sits with less movement against the wrist.',
  comfortable: 'A comfortable fit generally allows easy everyday movement.',
  relaxed: 'A relaxed fit generally sits with more movement on the wrist.',
});

class AraraSizeFitGuide extends HTMLElement {
  #abortController = new AbortController();
  #config;
  #dialogComponent;
  #dialog;
  #activeTrigger;
  #history = [];
  #currentView = '';
  #fingerUnit = 'mm';
  #wristUnit = 'cm';
  #calibrationHeight = 204;
  #ringIndex = 8;
  #resultValue = '';
  #necklaceValue = '';

  connectedCallback() {
    // Shopify can temporarily detach and reconnect product blocks while it
    // morphs the product section. A disconnected AbortSignal cannot be reused,
    // so create a fresh controller before binding this instance again.
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const configNode = this.querySelector('[data-arara-size-fit-config]');
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#dialog = this.querySelector('dialog');

    if (!configNode?.textContent || !this.#dialogComponent || !this.#dialog) return;

    try {
      this.#config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('ARARA Size Guide: invalid product configuration.', error);
      return;
    }

    this.#calibrationHeight = this.#readStoredCalibration();

    const { signal } = this.#abortController;
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#handleGuideClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.#dialogComponent.addEventListener('dialog:close', this.#handleDialogClose, { signal });

    this.#prepareKnownSizeGrid();
    this.#prepareRingConversionTable();
    this.#prepareInitialNecklaceState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleGuideClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target) return;

    const action = target.dataset.guideAction;
    const nativeValue = target.dataset.selectNativeValue;
    const knownSize = target.dataset.knownSize;
    const necklaceLength = target.dataset.necklaceLength;
    const fitPreference = target.dataset.fitPreference;
    const unit = target.dataset.guideUnit;

    if (action) this.#runAction(action);
    if (nativeValue) this.#selectNativeValue(nativeValue);
    if (knownSize) this.#showRingResult(this.#ringEntryForSize(Number(knownSize)), 'known');
    if (necklaceLength) this.#setNecklaceLength(necklaceLength);
    if (fitPreference) this.#setFitPreference(fitPreference);
    if (unit) this.#setUnit(target.dataset.guideUnitTarget, unit);
  };

  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.target.matches('[data-calibration-range]')) {
      this.#calibrationHeight = Number(event.target.value);
      this.#renderCalibration();
    } else if (event.target.matches('[data-ring-range]')) {
      this.#ringIndex = Number(event.target.value);
      this.#renderRingSizer();
    } else if (event.target.matches('[data-finger-input]')) {
      this.#setText('[data-finger-error]', '');
    } else if (event.target.matches('[data-wrist-input]')) {
      this.#validateWristInput(false);
    }
  };

  #handleVariantUpdate = (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || picker.dataset.productId !== this.#config?.productId) return;
    requestAnimationFrame(() => this.#syncNativeAvailability());
  };

  #handleDialogClose = () => {
    if (this.#activeTrigger?.isConnected) this.#activeTrigger.focus();
    this.#activeTrigger = undefined;
  };

  open(trigger) {
    if (!this.#config || !this.#dialogComponent?.showDialog) return;

    this.#activeTrigger = trigger;
    this.#history = [];
    this.#syncNativeAvailability();
    this.#resetUnits();

    if (this.#config.context === 'ring') {
      this.#showView(this.#config.adjustable ? 'ring-adjustable' : 'ring-landing', false);
    } else if (this.#config.context === 'bracelet') {
      this.#showView('bracelet-main', false);
    } else {
      this.#showView('necklace-main', false);
      this.#prepareInitialNecklaceState();
    }

    this.#dialogComponent.showDialog();
    requestAnimationFrame(() => this.querySelector('[data-guide-close]')?.focus());
  }

  #runAction(action) {
    switch (action) {
      case 'back':
        this.#goBack();
        break;
      case 'open-calibration':
        this.#showView('ring-calibration');
        requestAnimationFrame(() => {
          this.#updateCalibrationBounds();
          this.#renderCalibration();
        });
        break;
      case 'open-finger':
        this.#showView('ring-finger');
        break;
      case 'open-known':
        this.#showView('ring-known');
        break;
      case 'calibration-minus':
        this.#stepRange('[data-calibration-range]', -1);
        break;
      case 'calibration-plus':
        this.#stepRange('[data-calibration-range]', 1);
        break;
      case 'confirm-calibration':
        this.#storeCalibration();
        this.#showView('ring-sizer');
        this.#renderRingSizer();
        break;
      case 'ring-minus':
        this.#stepRange('[data-ring-range]', -1);
        break;
      case 'ring-plus':
        this.#stepRange('[data-ring-range]', 1);
        break;
      case 'use-ring-measurement':
        this.#showRingResult(RING_SIZE_TABLE[this.#ringIndex], 'ring');
        break;
      case 'calculate-finger':
        this.#calculateFingerSize();
        break;
      case 'select-result':
        if (this.#resultValue) this.#selectNativeValue(this.#resultValue);
        break;
      case 'select-necklace':
        if (this.#necklaceValue) this.#selectNativeValue(this.#necklaceValue);
        break;
    }
  }

  #showView(view, pushHistory = true) {
    if (pushHistory && this.#currentView && this.#currentView !== view) this.#history.push(this.#currentView);

    this.querySelectorAll('[data-guide-view]').forEach((section) => {
      section.hidden = section.dataset.guideView !== view;
    });

    this.#currentView = view;
    const backButton = this.querySelector('[data-guide-action="back"]');
    if (backButton) backButton.hidden = this.#history.length === 0;

    this.querySelector('.arara-size-fit-guide__body')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #goBack() {
    const previous = this.#history.pop();
    if (!previous) return;
    this.#showView(previous, false);
  }

  #updateCalibrationBounds() {
    const range = this.querySelector('[data-calibration-range]');
    if (!(range instanceof HTMLInputElement)) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const minimum = isMobile ? 230 : 140;
    // Avoid a calibration guide taller than a physical card can reasonably
    // appear on current phones, which would also bury the mobile controls.
    const maximum = isMobile ? Math.min(380, Math.max(340, Math.round(window.innerWidth * 0.96))) : 320;

    range.min = String(minimum);
    range.max = String(maximum);
    this.#calibrationHeight = Math.max(minimum, Math.min(this.#calibrationHeight, maximum));
    range.value = String(Math.round(this.#calibrationHeight));
  }

  #renderCalibration() {
    const card = this.querySelector('[data-bank-card]');
    if (!(card instanceof HTMLElement)) return;

    card.style.height = `${this.#calibrationHeight}px`;

    const stage = this.querySelector('[data-calibration-stage]');
    if (stage instanceof HTMLElement) {
      const stageHeight = Math.round(this.#calibrationHeight + 68);
      stage.style.setProperty('--arara-calibration-card-height', `${this.#calibrationHeight}px`);
      stage.style.setProperty('--arara-calibration-stage-height', `${stageHeight}px`);
    }
  }

  #renderRingSizer() {
    const entry = RING_SIZE_TABLE[this.#ringIndex] ?? RING_SIZE_TABLE[8];
    const pixelsPerMm = this.#calibrationHeight / CARD_REFERENCE_HEIGHT_MM;
    const circle = this.querySelector('[data-ring-circle]');
    if (circle instanceof HTMLElement) circle.style.width = `${entry.diameter * pixelsPerMm}px`;

    this.#setText('[data-ring-diameter]', `${this.#formatNumber(entry.diameter)} mm`);
    this.#setText('[data-ring-circumference]', `${this.#formatNumber(entry.circumference)} mm`);
    this.#setText('[data-ring-size]', this.#formatNumber(entry.size));
    this.#setText('[data-ring-size-badge]', `US ${this.#formatNumber(entry.size)}`);
    this.#setText('[data-ring-conversion-summary]', `US ${this.#formatNumber(entry.size)} · UK ${entry.uk} · Asia ${entry.asia ?? '—'}`);
    this.#setText('[data-ring-conversion-us]', this.#formatNumber(entry.size));
    this.#setText('[data-ring-conversion-uk]', entry.uk);
    this.#setText('[data-ring-conversion-asia]', entry.asia ?? '—');
    this.#setText('[data-ring-conversion-iso]', `${this.#formatNumber(entry.circumference)} mm`);
  }

  #prepareRingConversionTable() {
    const tableBody = this.querySelector('[data-ring-conversion-table]');
    if (!(tableBody instanceof HTMLElement)) return;

    const fragment = document.createDocumentFragment();
    for (const entry of RING_SIZE_TABLE) {
      const row = document.createElement('tr');
      const values = [
        this.#formatNumber(entry.size),
        entry.uk,
        entry.asia ?? '—',
        `${this.#formatNumber(entry.circumference)} mm`,
      ];

      for (const value of values) {
        const cell = document.createElement('td');
        cell.textContent = value;
        row.append(cell);
      }
      fragment.append(row);
    }
    tableBody.replaceChildren(fragment);
  }

  #prepareKnownSizeGrid() {
    const grid = this.querySelector('[data-known-size-grid]');
    if (!(grid instanceof HTMLElement)) return;

    const available = new Set(this.#numericProductSizes().map((item) => item.number));
    grid.replaceChildren();

    for (const entry of RING_SIZE_TABLE) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = this.#formatNumber(entry.size);
      button.dataset.knownSize = String(entry.size);
      button.dataset.productAvailable = String(available.has(entry.size));
      button.setAttribute('aria-label', `US ring size ${this.#formatNumber(entry.size)}${available.has(entry.size) ? ', available for this piece' : ''}`);
      grid.append(button);
    }
  }

  #calculateFingerSize() {
    const input = this.querySelector('[data-finger-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const raw = Number(input.value);
    const circumferenceMm = this.#fingerUnit === 'in' ? raw * 25.4 : raw;
    const minimum = RING_SIZE_TABLE[0].circumference;
    const maximum = RING_SIZE_TABLE[RING_SIZE_TABLE.length - 1].circumference;

    if (!Number.isFinite(circumferenceMm) || circumferenceMm < minimum || circumferenceMm > maximum) {
      this.#setText('[data-finger-error]', 'Please check your measurement and enter a value within the supported ring range.');
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    input.removeAttribute('aria-invalid');
    this.#setText('[data-finger-error]', '');
    const entry = RING_SIZE_TABLE.reduce((nearest, candidate) =>
      Math.abs(candidate.circumference - circumferenceMm) < Math.abs(nearest.circumference - circumferenceMm)
        ? candidate
        : nearest
    );
    this.#showRingResult(entry, 'finger', circumferenceMm);
  }

  #showRingResult(entry, source, measuredCircumference) {
    if (!entry) return;

    const sizes = this.#numericProductSizes();
    const exact = sizes.find((item) => Math.abs(item.number - entry.size) < 0.001);
    const nextHigher = sizes.find((item) => item.number > entry.size);
    const resultSize = this.querySelector('[data-result-size]');
    const resultCopy = this.querySelector('[data-result-copy]');
    const resultMeasurement = this.querySelector('[data-result-measurement]');
    const resultButton = this.querySelector('[data-result-value]');

    this.#resultValue = '';
    if (resultSize) resultSize.textContent = `US ${this.#formatNumber(entry.size)}`;

    if (measuredCircumference && resultMeasurement) {
      resultMeasurement.textContent = `${this.#formatNumber(measuredCircumference)} mm circumference · ${this.#formatNumber(entry.diameter)} mm inner diameter`;
    } else if (resultMeasurement) {
      resultMeasurement.textContent = source === 'known' ? `${this.#formatNumber(entry.diameter)} mm inner diameter · ${this.#formatNumber(entry.circumference)} mm circumference` : '';
    }

    if (!this.#config.optionName || sizes.length === 0) {
      if (resultCopy) resultCopy.textContent = 'This piece does not expose a selectable fixed-size option. Use this result as a fit reference.';
      this.#configureResultButton(resultButton, '', '');
    } else if (sizes.length === 1) {
      const only = sizes[0];
      if (resultCopy) {
        resultCopy.textContent = Math.abs(only.number - entry.size) < 0.001
          ? `US ${only.label} is the one fixed size available for this piece.`
          : `This piece is available in one fixed size: US ${only.label}. Your measured result is not converted into another option.`;
      }
      this.#resultValue = only.label;
      this.#configureResultButton(resultButton, only.label, `Select the available size ${only.label}`);
    } else if (exact) {
      if (resultCopy) resultCopy.textContent = `US ${exact.label} is available for this piece.`;
      this.#resultValue = exact.label;
      this.#configureResultButton(resultButton, exact.label, `Select size ${exact.label}`);
    } else if (nextHigher) {
      if (resultCopy) resultCopy.textContent = `Your measurement falls between available sizes. The next larger option for this piece is US ${nextHigher.label}. Please confirm before selecting.`;
      this.#resultValue = nextHigher.label;
      this.#configureResultButton(resultButton, nextHigher.label, `Select size ${nextHigher.label}`);
    } else {
      if (resultCopy) resultCopy.textContent = 'This measurement is above the fixed sizes currently available for this piece. No size has been selected.';
      this.#configureResultButton(resultButton, '', '');
    }

    this.#showView('ring-result');
  }

  #configureResultButton(button, value, label) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.hidden = !value;
    button.dataset.resultValue = value;
    if (label) button.textContent = label;
  }

  #numericProductSizes() {
    return (this.#config.values ?? [])
      .filter((item) => /^\d+(?:\.\d+)?$/.test(item.name.trim()))
      .map((item) => {
        const native = this.#findNativeControl(item.name);
        return {
          label: item.name.trim(),
          number: Number(item.name),
          available: native ? !native.disabled : item.available,
        };
      })
      .filter((item) => item.available !== false)
      .sort((a, b) => a.number - b.number);
  }

  #ringEntryForSize(size) {
    return RING_SIZE_TABLE.find((entry) => Math.abs(entry.size - size) < 0.001);
  }

  #setUnit(target, unit) {
    const previousUnit = target === 'finger' ? this.#fingerUnit : this.#wristUnit;
    const input = this.querySelector(target === 'finger' ? '[data-finger-input]' : '[data-wrist-input]');
    const label = this.querySelector(target === 'finger' ? '[data-finger-unit-label]' : '[data-wrist-unit-label]');

    if (input instanceof HTMLInputElement && input.value) {
      const current = Number(input.value);
      if (Number.isFinite(current)) {
        if (target === 'finger') input.value = this.#formatInput(unit === 'in' ? current / 25.4 : current * 25.4);
        else input.value = this.#formatInput(unit === 'in' ? current / 2.54 : current * 2.54);
      }
    }

    if (target === 'finger') this.#fingerUnit = unit;
    else this.#wristUnit = unit;
    if (label) label.textContent = unit;

    this.querySelectorAll(`[data-guide-unit-target="${target}"]`).forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.guideUnit === unit));
    });

    if (previousUnit !== unit && target === 'wrist') this.#validateWristInput(false);
  }

  #resetUnits() {
    const fingerInput = this.querySelector('[data-finger-input]');
    const wristInput = this.querySelector('[data-wrist-input]');
    if (fingerInput instanceof HTMLInputElement) fingerInput.value = '';
    if (wristInput instanceof HTMLInputElement) wristInput.value = '';
    this.#setText('[data-finger-error]', '');
    this.#setText('[data-wrist-error]', '');
    this.#fingerUnit = 'mm';
    this.#wristUnit = 'cm';
    this.#setUnit('finger', 'mm');
    this.#setUnit('wrist', 'cm');
  }

  #validateWristInput(showEmptyError) {
    const input = this.querySelector('[data-wrist-input]');
    if (!(input instanceof HTMLInputElement)) return false;

    if (!input.value && !showEmptyError) {
      this.#setText('[data-wrist-error]', '');
      input.removeAttribute('aria-invalid');
      return true;
    }

    const raw = Number(input.value);
    const centimeters = this.#wristUnit === 'in' ? raw * 2.54 : raw;
    const valid = Number.isFinite(centimeters) && centimeters >= 10 && centimeters <= 30;
    this.#setText('[data-wrist-error]', valid ? '' : 'Please check your measurement and try again.');
    if (valid) input.removeAttribute('aria-invalid');
    else input.setAttribute('aria-invalid', 'true');
    return valid;
  }

  #setFitPreference(preference) {
    this.querySelectorAll('[data-fit-preference]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.fitPreference === preference));
    });
    this.#setText('[data-fit-copy]', FIT_COPY[preference] ?? '');
  }

  #prepareInitialNecklaceState() {
    if (this.#config?.context !== 'necklace') return;
    const selected = this.#config.values?.find((item) => item.selected && item.available !== false)
      ?? this.#config.values?.find((item) => item.available !== false);
    if (selected) this.#setNecklaceLength(selected.name);
  }

  #setNecklaceLength(value) {
    const centimeters = this.#parseCentimeters(value);
    if (!Number.isFinite(centimeters)) return;

    this.#necklaceValue = value;
    this.querySelectorAll('[data-necklace-length]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.necklaceLength === value));
    });

    const drop = Math.max(122, Math.min(230, 122 + (centimeters - 35) * 5.4));
    const chain = this.querySelector('[data-necklace-chain]');
    const pendant = this.querySelector('[data-necklace-pendant]');
    chain?.setAttribute('d', `M90 86 Q140 ${drop} 190 86`);
    pendant?.setAttribute('cy', String(drop));
    this.#setText('[data-necklace-readout]', `${this.#formatNumber(centimeters)} cm / ${this.#formatNumber(centimeters / 2.54)} in`);

    const button = this.querySelector('[data-necklace-result]');
    if (button instanceof HTMLButtonElement) {
      button.hidden = false;
      button.dataset.necklaceResult = value;
      button.textContent = this.#config.values?.length === 1 ? 'Done' : `Select ${value}`;
    }
  }

  #syncNativeAvailability() {
    if (!this.#config?.optionName) return;

    this.querySelectorAll('[data-select-native-value], [data-necklace-length]').forEach((button) => {
      const value = button.dataset.selectNativeValue ?? button.dataset.necklaceLength;
      const native = this.#findNativeControl(value);
      if (native) button.toggleAttribute('disabled', native.disabled);
    });
    this.#prepareKnownSizeGrid();
  }

  async #selectNativeValue(value) {
    const native = this.#findNativeControl(value);

    if (!native) {
      if ((this.#config.values?.length ?? 0) === 1 && this.#config.values[0]?.name === value) {
        await this.#dialogComponent.closeDialog?.();
      }
      return;
    }
    if (native.disabled) return;

    await this.#dialogComponent.closeDialog?.();
    requestAnimationFrame(() => {
      if (!native.isConnected) {
        this.#findNativeControl(value)?.click();
      } else if (native instanceof HTMLSelectElement) {
        native.value = value;
        native.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        native.click();
      }
    });
  }

  #findNativeControl(value) {
    const picker = document.querySelector(`variant-picker[data-product-id="${CSS.escape(this.#config.productId)}"]`);
    if (!picker) return null;

    const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
      (input) => input.value === value && input.name.startsWith(`${this.#config.optionName}-`)
    );
    if (radio) return radio;

    return Array.from(picker.querySelectorAll('select')).find(
      (select) => select.name === `options[${this.#config.optionName}]` && Array.from(select.options).some((option) => option.value === value)
    ) ?? null;
  }

  #stepRange(selector, direction) {
    const range = this.querySelector(selector);
    if (!(range instanceof HTMLInputElement)) return;
    direction < 0 ? range.stepDown() : range.stepUp();
    range.dispatchEvent(new Event('input', { bubbles: true }));
  }

  #readStoredCalibration() {
    try {
      const stored = Number(localStorage.getItem(CALIBRATION_STORAGE_KEY));
      return Number.isFinite(stored) && stored >= 2.5 && stored <= 8
        ? stored * CARD_REFERENCE_HEIGHT_MM
        : this.#defaultCalibrationHeight();
    } catch (_error) {
      return this.#defaultCalibrationHeight();
    }
  }

  #storeCalibration() {
    try {
      const pixelsPerMm = this.#calibrationHeight / CARD_REFERENCE_HEIGHT_MM;
      localStorage.setItem(CALIBRATION_STORAGE_KEY, pixelsPerMm.toFixed(5));
    } catch (_error) {
      // Calibration still works for the current visit if storage is unavailable.
    }
  }

  #defaultCalibrationHeight() {
    if (!window.matchMedia('(max-width: 749px)').matches) return 204;
    // Start closer to the physical height of an ID-1 card on common modern
    // phones (including a 375 CSS-pixel iPhone viewport). The customer still
    // confirms the exact physical match with the slider.
    return Math.round(Math.max(292, Math.min(338, window.innerWidth * 0.86)));
  }

  #parseCentimeters(value) {
    const match = String(value).trim().match(/^(\d+(?:\.\d+)?)\s*cm$/i);
    return match ? Number(match[1]) : Number.NaN;
  }

  #formatNumber(value) {
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 });
  }

  #formatInput(value) {
    return Number(value).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }
}

if (!customElements.get('arara-size-fit-guide')) {
  customElements.define('arara-size-fit-guide', AraraSizeFitGuide);
}

// Product sections are morphed by the theme and may replace a guide instance.
// Keep the external trigger listener stable and resolve the current instance
// at click time instead of binding the trigger to a disposable component.
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-arara-size-guide-trigger]') : null;
  const guideId = trigger?.dataset.araraSizeGuideTrigger;
  if (!trigger || !guideId) return;

  const guide = Array.from(document.querySelectorAll('arara-size-fit-guide')).find(
    (element) => element.dataset.guideId === guideId
  );
  if (!(guide instanceof AraraSizeFitGuide)) return;

  event.preventDefault();
  guide.open(trigger);
});

export { RING_SIZE_TABLE, BRACELET_FIT_PROFILES };
